import * as React from "react";
import * as _ from "underscore";
import { Button } from "carbon-components-react";
import { Table } from "react-bootstrap";
import { connect } from "react-redux";

import { getFileDownloadBaseURL } from "config";
import { DocTypes } from "@eave/shared/types/api";
import APIClient from "web_modules/APIClient";
import LabeledInput from "web_modules/LabeledInput";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import { mapDispatchToProps } from "./selectors";

interface HurdleEditorProps extends HurdleProps {
  createHurdleOverride: Function;
  loadApp: Function;
  updateHurdleOverride: Function;
}

const LSFMTypesOfFraud = {
  id_theft: {
    fraud_type: "id_theft", // same as key
    display_text: "ID Theft",
    key_indicators: [
      "SSN issued recently or death claim filed under SSN",
      "Income and assets are frequently misrepresented",
      "Employment and/or addresses on credit report do not match 1003",
      "Falsified POA to obtain mortgage and cash-out on property",
      `Family fraud- using different suffix (jr, sr, I II II) to purchase or refinance property \
under relatives without permission`,
      "Credit patterns are inconsistent with income, assets, age and/or education",
    ],
    trigger_display_text: "Flagged only if High severity alert exists in the Identity section",
  },
  income_employment_misrepresentation: {
    fraud_type: "income_employment_misrepresentation", // same as key
    display_text: "Income/Employment Misrepresentation",
    key_indicators: [
      `Employer's address is a post office box, the property address, or borrower's current \
 residence`,
      "Pay period dates overlap and/or don't correspond with other documentation",
      "Handwritten check stubs and W2s. Unable to verify Employer through 3rd party website",
      "Income documentation do not coincide with 4506-T Transcript's finding",
      "Income appears to be out of line with type of employment",
      "White-outs or cross-outs on income/employment documents",
      `Abnormalities in paycheck numbering (e.g. borrower's 9/1-9/15 paystub is check #200, then \
Eave receives the 9/16-9/30 paycheck and it is check #150)`,
      "Employer phone number is disconnected or is a private residence",
    ],
    trigger_display_text: `Flagged only if EITHER Data-entry of uploaded paystubs/w-2 (ingestion) \
flags a 'fudging of the document'  OR if a high severity alert is present in the INCOME/EMPLOYMENT \
Section of LSFM`,
  },
  occupancy_misrepresentation: {
    fraud_type: "occupancy_misrepresentation", // same as key
    display_text: "Occupancy Misrepresentation",
    key_indicators: [
      `Borrower is downgrading from a larger or more expensive home and owns multiple rental \
properties`,
      "Different address reported on credit report",
      `Hazard insurance declarations page reflects rent loss coverage or has different mailing \
address`,
      "Rental property listed on application is more expensive than subject property",
      `Different mailing address or P.O Box on borrower's bank statements, pay advices, etc \
AE-P.O.Box`,
      `Puchase transaction- Subject is a second home, yet the borrower's profile (income and \
asset) indicates a probably financial strain with the expected upkeep of second home AE`,
      `Refinance transaction- Subject is a second home, yet there is independent evidence other \
parties reside at the subject property, or that the subject property is available fo rent (check \
previous Deed of Trust to see if there are any riders showing as investment property`,
      "Refinance transaction- Title Commitment shows a different vesting/ownership",
      "Significant or unrealistic commute distance",
      "Change in valuation after natural disaster",
      "Condo- sell one and use that sales price to inflate other units in the project",
      "Different address reflected on Fraud Guard as owner occupied property",
    ],
    trigger_display_text: `Flagged in all cases where LSFM presents a high severity alert in \
OCCUPANCY`,
  },
  shot_gunning: {
    fraud_type: "shot_gunning", // same as key
    display_text: "Shot Gunning",
    key_indicators: [
      `Definition: The opening of multiple loans using the same property’s collateral within a \
short period of time (usually 30 days) for the purpose of withdrawing cash proceeds in excess of \
value of the property`,
      "Borrower has no intention to move into the property",
      "Borrower's signatures differ throughout the file",
      "Borrower's income/employment is misrepresented",
      `Documentation provided are tampered in order to qualify for each individual loans applied \
for`,
      "A common bank account is used by various borrowers",
    ],
    trigger_display_text: `Flagged IF borrower has >10 distinct real estate enquiries in the last \
30 days OR if Income/Employment section in LSFM has high severity alerts OR bank account is found \
to not be owned by Borrower or Co-Borrower`,
  },
  foreclosure_bailout: {
    fraud_type: "foreclosure_bailout", // same as key
    display_text: "Foreclosure Bailout",
    key_indicators: [
      `Definition: Seller pays large financial incentives to buyer, and facilitates an inflated \
loan amount by increasing the sales price, concealing the incentive and utilizing a fraudulently \
inflated appraisal`,
      "The builder is willing to 'do anything' to sell property",
      "The borrower is barely qualified or unqualified",
      "The sales price and appraisal are inflated",
      "No-money-down sales are included",
      "'Silent' second mortgages are involved",
      "The sales price is adjusted upwards",
      "The source of funds is questionable",
      "Closing Disclosure and/or purchase contract reference secondary financing that's unknown",
      "Parties to the transaction are affiliated",
      "Multiple sales to same person",
      "Typically involves new construction or new condo conversion",
      "Builder's marketing material advertises rent credit to investors and/or payment credit",
      "High Debt-to-income ratio and Loan to Value with mortgage insurance",
      "Mulitple chain of title within the past 24 month between family and friends",
      "Arm's length transaction ( there's a relationship between seller and buyer)",
      "CD reflects unexplained payouts or inflated commission (paid outside closing to buyer)",
    ],
    trigger_display_text: `IF High Severity Alerts are present in the Property Section or Third \
Party Section of LSFM OR if PC has many flags on Seller Financing, Rentback for > 6 months OR if \
property has been in foreclosure proceedings OR been sold atleast once in the last 12 months OR \
secondary financing is mentioned in the PC  OR borrower has insufficient funds and attempts to \
get a gift from seller, this Fraud category must be flagged and cleared by an Underwriter`,
  },
  property_flipping: {
    fraud_type: "property_flipping", // same as key
    display_text: "Property Flipping",
    key_indicators: [
      `Definition: Illegal property flipping occurs when property is purchased and resold quickly \
at an artificially inflated price, utilizing a fraudulently inflated appraisal`,
      "Multiple ownership changes/transfers within 24 months",
      "Two or more closings occur almost simultaneously",
      "The property has been owned for a short time by the seller",
      "The property seller is not on title",
      "There is a reference to double escrow or other CD form",
      "Parties to the transaction are affiliated",
      "Up and down fluctuation of sales price over short period of time",
      "Multiple investment properties obtained by same buyer within short timeframe",
      "'Purchases' disguised as 'refinances' to circumvent a down payment",
      "Property seller is an entity/corporation",
      "Unusual cash payouts at closing on CD",
      "Comparable sales on appraisal are previously flipped properties",
      "No real estate agent",
      "Property was recently in foreclosure or acquired at REO sale at a much lower price",
      "The appraised value is fraudulently inflated",
      "The appraiser frequently uses other property flips as comparable",
      "Appraisal report shows no improvements made to the subject property",
      "Owner listed on appraisal and/or title may not match the seller on the sales contract",
    ],
    trigger_display_text: `IF High Severity Alerts are present in the Property Section or Market \
Section or Third Party Section of LSFM OR if the title commitment has > 3 transactions in the last \
24 months OR if Seller in PC does not match seller in Title Commitment OR if seller is an LLC/LP \
with no principal names revealed in the PC OR if Borrower has obtained a recent property (new \
mortgage on a different property in the last 6 months) OR if the refinance is being applied for \
within 2 months of purchase being settled, this Fraud category must be flagged for an Underwriter \
to clear`,
  },
  straw_purchases: {
    fraud_type: "straw_purchases", // same as key
    display_text: "Straw Purchases",
    key_indicators: [
      `Definition: A person used to buy property in order to conceal the actual owner. The straw \
buyer does not intend to occupy the property or make payments and often deeds the property to the \
other individual immediately after closing. The straw buyer is usually compensated for use of \
his/her identity. In some cases, fake identities are explicitly created (for e.g. accidental \
credit card offers to babies)`,
      "A quitclaim deed is used either right before, or soon after, loan closing",
      "Investment property is represented as owner-occupied",
      "Someone signed the 1003 on the borrower's behalf",
      "Names were added to the purchase contract",
      "Sale involves a relative or otherwise related party",
      "Earnest Money Deposit is made out by someone different than the buyer or the Employer",
      "Borrower is representing him/herself as buyer's agent",
      "There is an indication of default by the property seller",
      "Borrower is using a Power-of-Attorney for closing docs",
      "Mortgage payments are made by an entity other than the borrower",
      "The loan is usually an early payment default",
      "First-time homebuyer with substantial increase in housing expense",
      "Buyer doesn't intend to occupy – long commute, size, condition of property, etc.",
      "Borrower's employee is acting as buyer's agent",
      "Income, savings and/or credit is inconsistent with borrower's overall profile",
      "Inconsistent signatures throughout the file",
    ],
    trigger_display_text: `IF Third Party high severity flags are present in LSFM OR if Conflicts \
of Interest flags are triggered OR if borrower is using a PoA despite no extenuating circumstances \
(like military duty or occupation that does not require foreign travel or no medical expense \
traces in bank statements for medical reasons behind use of PoA) OR Occupancy check fails OR \
signatures are inconsistent OR title is being taken by an additional name that is not a \
non-borrowing spouse, then this fraud category needs to be flagged for the Underwriter to Clear`,
  },
  ponzi_investment_clubs_chunking: {
    fraud_type: "ponzi_investment_clubs_chunking", // same as key
    display_text: "Ponzi / Investment Clubs / Chunking",
    key_indicators: [
      `Definition: The sale of properties at artificially inflated prices, pitched as investment \
opportunities to naïve real estate investors who are promised improbably high returns and low \
risks`,
      "No real estate agent (club recruits buyers and/or non arms-length transaction",
      "Property was recently in foreclosure or acquired at REO sale at a much lower price",
      "Borrower may have paid a membership fee to participate in the 'club'",
      "First-time landlord and/or non-savvy investors (no history of managing property)",
      "Seller offers to manage the rental properties",
      "Borrower may have been told that the seller or the 'club' will make payments",
      `Borrower purchased multiple properties simultaneously, but did not disclose other loans in \
process to their lender (shot gunning). Monitor credit inquiries for indications`,
      "Appraised value is fraudulently inflated",
      "Collusion among appraisers, closing agents, investment club members",
    ],
    trigger_display_text: `IF NO Buyers Side Agent or NO (Sellers Side agent AND NOT an FSBO) OR \
Property well outside the metro area where there are no local settlement agents within 10 mile \
radius (from Closing Corp) OR Third Party High Severity Flags present in LSFM, flag this fraud \
category for Underwriter to clear`,
  },
};

const LSFMFraudTypeAlertCategoryTriggers = {
  IDENTITY: ["id_theft"],
  INCOME: ["income_employment_misrepresentation", "shot_gunning"],
  BORROWER: ["occupancy_misrepresentation"],
  PROPERTY: ["foreclosure_bailout", "property_flipping"],
  BROKER: [
    "foreclosure_bailout",
    "property_flipping",
    "straw_purchases",
    "ponzi_investment_clubs_chunking",
  ],
  MARKET: ["property_flipping"],
};

class FraudHurdleEditor extends React.Component<HurdleEditorProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      lsfmOnComponentInit: this.props.app.lsfm_summary != null,
    };
    const hurdleOverrideValues = this.props.hurdle.override
      ? this.props.hurdle.override.values
      : {};
    Object.assign(this.state, hurdleOverrideValues);
  }

  // TODO hurdle override functionality copied from SpecialSituations.tsx - consolidate
  buttons(field) {
    const value = this.props.hurdle.override ? this.props.hurdle.override.values[field] : null;
    return (
      <div className="button-menu horizontal">
        <Button
          onClick={this.postOverride.bind(this, field, true)}
          className={value === true ? "active" : null}
        >
          Yes
        </Button>
        <Button
          onClick={this.postOverride.bind(this, field, false)}
          className={value === false ? "active" : null}
        >
          No
        </Button>
      </div>
    );
  }

  message(field) {
    const messageField = `${field}_message`;
    return (
      <div>
        <LabeledInput
          inputType="textarea"
          inputName={messageField}
          placeholder="Please enter additional information"
          inputValue={this.state[messageField]}
          onChange={this.handleOnChange.bind(this)}
        />
        <Button onClick={this.postMessageOverride.bind(this, messageField)}>Save</Button>
      </div>
    );
  }

  handleOnChange(e) {
    const [field, value] = [e.target.name, e.target.value];
    this.setState({ [field]: value });
  }

  postMessageOverride(messageField) {
    this.postOverride(messageField, this.state[messageField]);
  }

  postOverride(field, value) {
    const { app, createHurdleOverride, hurdle, updateHurdleOverride } = this.props;
    const { override } = hurdle;
    const overrideValues = override && override.values ? override.values : {};
    overrideValues[field] = value;

    if (!override) {
      createHurdleOverride({
        appId: app.id,
        hurdleName: hurdle.name,
        hurdleOverrideValues: overrideValues,
      });
    } else if (override) {
      updateHurdleOverride({
        appId: app.id,
        hurdleOverrideId: hurdle.override.id,
        hurdleOverrideValues: overrideValues,
      });
    }
  }

  lsfm(): any {
    const { app } = this.props;
    return _.chain(app.documents)
      .filter((d: any) => d.type === DocTypes.LoansafeAppraisalManagerReport)
      .sortBy((d: any) => parseInt(d.id, 10))
      .first()
      .value();
  }

  requestLSFM = () => {
    APIClient.post(`/${this.props.app.id}/lsfm`, {}).then((response: any) => {
      this.setState({
        orderStatus: response.status,
      });
    });
  };

  checkLSFM = () => {
    const { app, loadApp } = this.props;
    this.setState({
      checkedAt: new Date().toUTCString(),
    });
    loadApp(app.id);
  };

  renderLSFM() {
    const lsfm = this.lsfm();
    return (
      <div>
        {lsfm && lsfm.s3_key && (
          <div style={{ display: "flex", width: "100%" }}>
            <iframe src={getFileDownloadBaseURL(lsfm.s3_key)} width="100%" height="520" />
          </div>
        )}
      </div>
    );
  }

  renderLSFMRequestButton() {
    const { orderStatus, checkedAt } = this.state;
    const {
      app: { lsfm_summary },
    } = this.props;

    if (orderStatus === "in_progress") {
      return (
        <div>
          <Button bsSize="small" onClick={this.checkLSFM}>
            Check LSFM status
          </Button>
          {checkedAt && <p>Checked at: {checkedAt}</p>}
        </div>
      );
    }

    if (orderStatus === "cannot_order") {
      return (
        <div>
          <p>Cannot order LSFM, see ops slack channel for reason</p>
          {checkedAt && <p>Checked at: {checkedAt}</p>}
        </div>
      );
    }

    const buttonText = !lsfm_summary
      ? "Request LSFM"
      : "Reorder LSFM (this will replace the existing LSFM)";

    return (
      <Button bsSize="small" onClick={this.requestLSFM}>
        {buttonText}
      </Button>
    );
  }

  typesOfFraudGuidelines() {
    // first get a list of unique categories
    const lsfmSummary: any = this.props.app.lsfm_summary;
    const alertCategories = _.uniq(_.pluck(lsfmSummary.high_severity_alerts, "category"));
    // then get a list of unique fraud type objects
    const fraudGuidelines = [];
    const fraudTypeAdded = new Set();
    for (const category of alertCategories) {
      const fraudTypeKeys = LSFMFraudTypeAlertCategoryTriggers[category] || [];
      for (const fraudTypeKey of fraudTypeKeys) {
        if (!fraudTypeAdded.has(fraudTypeKey)) {
          fraudGuidelines.push(LSFMTypesOfFraud[fraudTypeKey]);
          fraudTypeAdded.add(fraudTypeKey);
        }
      }
    }
    return fraudGuidelines;
  }

  renderLSFMData() {
    const lsfmSummary: any = this.props.app.lsfm_summary;
    if (lsfmSummary) {
      let alertTableHeading = "High Severity Alerts:";
      // TODO need to use redux to reload when we get the lsfm
      if (!this.state.lsfmOnComponentInit) {
        alertTableHeading += " (please refresh the page to reload flags in the sidebar)";
      }
      return (
        <div>
          <h3>LSFM Score: {lsfmSummary.score}</h3>
          {lsfmSummary.high_severity_alerts && (
            <div>
              <br />
              <h4>{alertTableHeading}</h4>
              <Table condensed bordered striped hover>
                <thead>
                  <tr>
                    <th>Alert ID</th>
                    <th>Category</th>
                    <th>Message</th>
                    <th>Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {lsfmSummary.high_severity_alerts.map((alert, index) => (
                    <tr key={index}>
                      <td>{alert.external_id}</td>
                      <td>{alert.category_display_text}</td>
                      <td>{alert.message}</td>
                      <td>{alert.recommendation}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <br />
              <h4>Underwriter Review</h4>
              <Table condensed bordered striped hover>
                <thead />
                <tbody>
                  {this.typesOfFraudGuidelines().map((fraudType, index) => (
                    <tr key={index}>
                      <td title={fraudType.trigger_display_text}>
                        <h4>Is there evidence of {fraudType.display_text}?</h4>
                        <ul>
                          {fraudType.key_indicators.map((indicator, indicatorIndex) => (
                            <li key={indicatorIndex} className="checklist-item">
                              {indicator}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td style={{ width: "25%" }}>
                        {this.buttons(fraudType.fraud_type)}
                        {this.message(fraudType.fraud_type)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <br />
            </div>
          )}
        </div>
      );
    }
    return null;
  }

  render() {
    const { app, hurdle, onHide } = this.props;
    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        {this.renderLSFMRequestButton()}
        {this.renderLSFMData()}
        {this.renderLSFM()}
      </HurdleView>
    );
  }
}

export default connect<any, any, any>(null, mapDispatchToProps)(FraudHurdleEditor);
